import { type Websocket, WebsocketEvent } from '@atlas/collaboration/websocket';
import { Telemetry } from '@atlas/observability';
import { getOwner, onCleanup } from 'solid-js';
import { v7 as uuidv7 } from 'uuid';
import { z } from 'zod';

export type WebSocketJobConfig<T, R, D, U> = {
  data: D;
  action: string;
  validateResponse: (data: unknown) => data is U;
  processResult: (data: U, jobId: string) => Promise<T | undefined>;
  handleSuccess: (result: T) => Promise<R>;
};

const responseEnvelope = z.object({
  macroRequestId: z.string(),
  status: z.string().optional(),
  jobId: z.string().optional(),
  data: z
    .object({ error: z.boolean().optional(), data: z.unknown().optional() })
    .optional(),
});

/** One request owns its listeners and settles even without a Solid owner. */
export function runWebSocketJob<T, R, D, U>(
  socket: Pick<Websocket, 'send' | 'addEventListener' | 'removeEventListener'>,
  config: WebSocketJobConfig<T, R, D, U>,
  timeoutMs = 45_000
): Promise<R> {
  const span = Telemetry.clientSpan('pdf.processing_job');
  span.setAttr('job.action', config.action);
  const requestId = uuidv7();
  return new Promise((resolve, reject) => {
    let settled = false;
    let processing = false;
    const finish = (error?: Error, result?: R) => {
      if (settled) return;
      settled = true;
      clearTimeout(deadline);
      socket.removeEventListener(WebsocketEvent.Message, messageHandler);
      socket.removeEventListener(WebsocketEvent.Close, closed);
      if (error) {
        span.error(error.message);
        reject(error);
      } else if (result !== undefined) resolve(result);
      else reject(new Error('PDF job returned no result'));
      span.end();
    };
    const closed = () => finish(new Error('PDF processing connection closed'));
    const messageHandler = async (_socket: Websocket, event: MessageEvent) => {
      if (settled || event.data === 'pong') return;
      let raw: unknown;
      try {
        raw = JSON.parse(event.data);
      } catch {
        return;
      }
      try {
        // Malformed/unrelated events cannot settle someone else's request.
        if (
          !raw ||
          typeof raw !== 'object' ||
          !('macroRequestId' in raw) ||
          raw.macroRequestId !== requestId
        )
          return;
        const parsed = responseEnvelope.safeParse(raw);
        if (!parsed.success) throw new Error('Invalid PDF job response');
        const message = parsed.data;
        if (message.status === 'Error' || message.data?.error)
          throw new Error('PDF processing failed');
        if (message.data?.data === undefined) return;
        if (processing) return;
        const jobId = message.jobId;
        const responseData = message.data.data;
        if (!jobId || !config.validateResponse(responseData))
          throw new Error('Invalid PDF job result');
        processing = true;
        const result = await span.run(() =>
          config.processResult(responseData, jobId)
        );
        if (settled) return;
        if (result === undefined) throw new Error('PDF job returned no result');
        const output = await config.handleSuccess(result);
        finish(undefined, output);
      } catch {
        finish(new Error('PDF processing failed'));
      }
    };
    const deadline = setTimeout(
      () => finish(new Error('PDF processing timed out')),
      timeoutMs
    );
    socket.addEventListener(WebsocketEvent.Message, messageHandler);
    socket.addEventListener(WebsocketEvent.Close, closed);
    if (getOwner())
      onCleanup(() => finish(new Error('PDF processing cancelled')));
    try {
      socket.send(
        JSON.stringify({
          requestId,
          action: config.action,
          data: config.data,
          traceparent: span.traceparent(),
        })
      );
    } catch {
      finish(new Error('PDF processing request failed'));
    }
  });
}
